import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Snackbar,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { BorderBottom, Info } from "@mui/icons-material";
import apiService from "../components/ReasuableFuncations/api";
import AddIcon from "@mui/icons-material/Add";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Move AddIndicatorDialog outside of the component render to prevent remounting
const AddIndicatorDialog = React.memo(
  ({ open, onClose, value, onChange, onSubmit }) => (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          padding: 2,
          minHeight: "200px",
        },
      }}
      TransitionProps={{
        unmountOnExit: false, // Prevent unmounting when dialog closes
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Add New Indicator</Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Indicator Name"
          fullWidth
          variant="outlined"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={!value.trim() && value !== ""}
          helperText={
            !value.trim() && value !== ""
              ? "Indicator name cannot be empty"
              : ""
          }
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          color="primary"
          variant="contained"
          disabled={!value.trim()}
        >
          Add Indicator
        </Button>
      </DialogActions>
    </Dialog>
  )
);

const DataCollection = () => {
  // Add new state for user type at the top with other state declarations
  const [userType, setUserType] = useState('');
  
  // Remove unused state variables
  const [formType, setFormType] = useState("outcome");
  const [formData, setFormData] = useState({
    projectName: "",
    type: "outcome",
    indicators: [],
  });
  const [outcomeState, setOutcomeState] = useState([{}]);
  const [outcomeError, setOutcomeError] = useState([{}]);

  const [outputState, setOutputState] = useState([{}]);
  const [outputError, setOutputError] = useState([{}]);

  const [activityState, setActivityState] = useState([{}]);
  const [activityError, setActivityError] = useState([]);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [gender, setGender] = useState(false);
  const [ageGroup, setAgeGroup] = useState(false);
  const [disability, setDisability] = useState(false);

  const [currency, setCurrency] = useState("ZAR");
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [indicatorDialogOpen, setIndicatorDialogOpen] = useState(false);
  const [newIndicator, setNewIndicator] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [outputData, setOutputData] = useState({}); // Add missing state

  const currencyOptions = [
    { symbol: "$", code: "USD", name: "US Dollar" },
    { symbol: "€", code: "EUR", name: "Euro" },
    { symbol: "£", code: "GBP", name: "British Pound" },
    { symbol: "₹", code: "INR", name: "Indian Rupee" },
    { symbol: "R", code: "ZAR", name: "South African Rand" },
    { symbol: "KSh", code: "KES", name: "Kenyan Shilling" },
    { symbol: "USh", code: "UGX", name: "Ugandan Shilling" },
    { symbol: "TSh", code: "TZS", name: "Tanzanian Shilling" },
    { symbol: "ETB", code: "ETB", name: "Ethiopian Birr" },
  ];

  const exchangeRates = {
    USD: 1,
    EUR: 1.08,
    GBP: 1.3,
    INR: 0.012,
    ZAR: 0.057,
    KES: 0.0077,
    UGX: 0.00027,
    TZS: 0.00037,
    ETB: 0.0082,
  };

  const fetchData = React.useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) {
      console.error("User data not found.");
      return;
    }

    setIsLoading(true);
    const {
      selectedOption,
      regionalNetworkOption,
      memberStateOption,
      countryOption,
    } = userData;
    let url = "/api/output-data";

    try {
      let response;
      if (selectedOption === "Comesa") {
        response = await apiService.get(
          `${url}?projectId=${formData.projectName}`
        );
      } else if (
        selectedOption === "Regional Network" &&
        regionalNetworkOption
      ) {
        response = await apiService.get(
          `${url}?regionalNetwork=${regionalNetworkOption}&projectId=${formData.projectName}`
        );
      } else if (selectedOption === "Member State" && memberStateOption) {
        response = await apiService.get(
          `${url}?country=${countryOption}&projectId=${formData.projectName}`
        );
      }

      if (response?.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error fetching output data");
      setSnackOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, []); // Add any dependencies if needed

  const convertToUSD = (value, fromCurrency) => {
    if (!value || !fromCurrency) return null;
    return (parseFloat(value) * exchangeRates[fromCurrency]).toFixed(2);
  };

  const getCurrencySymbol = (currencyCode) => {
    const currency = currencyOptions.find((c) => c.code === currencyCode);
    return currency ? currency.symbol : "";
  };

  const fetchProjects = React.useCallback(async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    const {
      selectedOption,
      regionalNetworkOption,
      memberStateOption,
      countryOption,
    } = userData;
    let url = "/api/projects-stake";

    try {
      let response;
      if (selectedOption === "Comesa") {
        response = await apiService.get("/api/project-data");
      } else if (
        selectedOption === "Regional Network" &&
        regionalNetworkOption
      ) {
        response = await apiService.get(
          `${url}?regionalNetwork=${regionalNetworkOption}&formType=${formType}`
        );
      } else if (selectedOption === "Member State" && memberStateOption) {
        response = await apiService.get(
          `${url}?country=${countryOption}&formType=${formType}`
        );
      }

      if (response && !response.statusText) {
        throw new Error("Failed to fetch projects");
      }

      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Error fetching projects. Please try again.");
      setSnackOpen(true);
    }
  }, [formType]); // Add dependencies

  useEffect(() => {
    fetchProjects();
    setFormData((prevData) => ({ ...prevData, type: formType }));
  }, [formType]);

  useEffect(() => {
    fetchData();
    setOutputData((prevData) => ({ ...prevData, type: formType }));
  }, [formType]);

  useEffect(() => {
    const fetchIndicators = async () => {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) {
        console.error("User data not found.");
        return;
      }

      const {
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
      } = userData;
      try {
        // Build query string with parameters
        const queryParams = new URLSearchParams({
          selectedOption,
          ...(regionalNetworkOption && { regionalNetworkOption }),
          ...(memberStateOption && { memberStateOption }),
          ...(countryOption && { countryOption }),
          isCategory: "true",
        });

        const response = await apiService.get(`/indicators?${queryParams}`);

        if (response?.data && Array.isArray(response.data)) {
          setIndicators(response.data);
        } else {
          // Fallback to default indicators if API response is not an array
          setIndicators([
            "Life of project target",
            "Annual target",
            "Target for reporting period",
            "Actual Result",
            "Variance",
          ]);
        }
      } catch (error) {
        console.error("Error fetching indicators:", error);
        // Fallback to default indicators if API fails
        setIndicators([
          "Life of project target",
          "Annual target",
          "Target for reporting period",
          "Actual Result",
          "Variance",
        ]);
      }
    };

    fetchIndicators();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  // Add effect to get user type when component mounts
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) {
      setUserType(userData.selectedOption);
    }
  }, []);

  const validateForm = () => {
    const errors = {};

    // Base validation for project name
    if (!formData.projectName) {
      errors.projectName = "Project Name is required";
    }

    setFormErrors(errors);

    // Log validation results for debugging
    if (Object.keys(errors).length > 0) {
      console.log("Validation failed:", errors);
      setError("Please select project name");
      setSnackOpen(true);
    }

    return Object.keys(errors).length === 0;
  };

  const validateOutcomeFields = () => {
    const errors = {};

    outcomeState.forEach((outcome, index) => {
      if (!outcome.name || outcome.name.trim() === "") {
        errors[`name_${index}`] = "Outcome name is required";
      }

      // if (parseInt(outcome.baseline) < parseInt(outcome.target)) {
      //   errors[`baseline_${index}`] = "Baseline cannot be greater than target";
      // }

      if (!outcome.target || outcome.target.trim() === "") {
        if (parseInt(outcome.target) > parseInt(outcome.baseline)) {
          errors[`target_${index}`] = "Target must be greater than baseline";
        }
      }
      if (!outcome.current || outcome.current.trim() === "") {
        errors[`current_${index}`] = "Current is required";
      }
      if (!outcome.achievement || outcome.achievement.trim() === "") {
        errors[`achievement_${index}`] = "Achievement is required";
      }
      if (!outcome.justify || outcome.justify.trim() === "") {
        errors[`justify_${index}`] = "Justification is required";
      }

      if (new Date(outcome.dateAchieved) > new Date(outcome.targetDate)) {
        errors[`dateAchieved_${index}`] =
          "Date achieved cannot be later than the target date";
      } else {
        delete errors[`dateAchieved_${index}`];
      }
      
      if (new Date(outcome.targetDate) < new Date(outcome.dateAchieved)) {
        errors[`targetDate_${index}`] =
          "Target date cannot be earlier than the date achieved";
      } else {
        delete errors[`targetDate_${index}`];
      }
      

      if (!outcome.actualOutputDate || outcome.actualOutputDate.trim() === "") {
        errors[`actualOutputDate_${index}`] = "Reporting Date is required";
      } else {
        const reportingDate = new Date(outcome.actualOutputDate);
        const today = new Date();
        const targetDate = new Date(outcome.targetDate);
        today.setHours(0, 0, 0, 0); // Reset time part for accurate date comparison
      
        if (reportingDate < today) {
          errors[`actualOutputDate_${index}`] = "Reporting Date cannot be earlier than today";
        }
        if (reportingDate > targetDate) {
          errors[`actualOutputDate_${index}`] = "Reporting Date cannot be later than Target Date";
        }
      }
      if (!outcome.comments || outcome.comments.trim() === "") {
        errors[`comments_${index}`] = "Comments is required";
      }
    });

    return errors;
  };

  const validateOutputFields = () => {
    const errors = {};

    outputState.forEach((output, index) => {
      if (!output.name || output.name.trim() === "") {
        errors[`name_${index}`] = "Output name is required";
      }
      if (parseInt(output.baseline) > parseInt(output.target)) {
        errors[`baseline_${index}`] = "Baseline cannot be greater than target";
      }

      if (parseInt(output.baseline) > parseInt(output.target)) {
        errors[`target_${index}`] = "Target cannot be less than baseline";
      }

      if (!output.current || output.current.trim() === "") {
        errors[`current_${index}`] = "Current is required";
      }
      if (!output.achievement || output.achievement.trim() === "") {
        errors[`achievement_${index}`] = "Achievement is required";
      }
      if (!output.justify || output.justify.trim() === "") {
        errors[`justify_${index}`] = "Justification is required";
      }

      if (new Date(output.dateAchieved) > new Date(output.targetDate)) {
        errors[`dateAchieved_${index}`] =
          "Date achieved cannot be later than the target date";
      } else {
        delete errors[`dateAchieved_${index}`];
      }
      
      if (new Date(output.baseline) > new Date(output.targetDate)) {
        errors[`targetDate_${index}`] =
          "Baseline date cannot be later than the target date";
      } else {
        delete errors[`targetDate_${index}`];
      }
      

      if (!output.actualOutputDate || output.actualOutputDate.trim() === "") {
        errors[`actualOutputDate_${index}`] = "Reporting Date is required";
      } else {
        const reportingDate = new Date(output.actualOutputDate);
        const today = new Date();
        const targetDate = new Date(output.targetDate);
        today.setHours(0, 0, 0, 0); // Reset time part for accurate date comparison
      
        if (reportingDate < today) {
          errors[`actualOutputDate_${index}`] = "Reporting Date cannot be earlier than today";
        }
        if (reportingDate > targetDate) {
          errors[`actualOutputDate_${index}`] = "Reporting Date cannot be later than Target Date";
        }
      }
      if (!output.comments || output.comments.trim() === "") {
        errors[`comments_${index}`] = "Comments is required";
      }
    });

    return errors;
  };

  const validateActivityFields = () => {
    const errors = {};

    activityState.forEach((activity, index) => {
      if (!activity.activityname || activity.activityname.trim() === "") {
        errors[`name_${index}`] = "Activity name is required";
      }
      if (!activity.description || activity.description.trim() === "") {
        errors[`description_${index}`] = "Activity description is required";
      }
      if (!activity.target || activity.target.trim() === "") {
        errors[`target_${index}`] = "Target is required";
      }
      if (!activity.current || activity.current.trim() === "") {
        errors[`current_${index}`] = "Current is required";
      }
      if (!activity.achievement || activity.achievement.trim() === "") {
        errors[`achievement_${index}`] = "Achievement is required";
      }
      if (!activity.justify || activity.justify.trim() === "") {
        errors[`justify_${index}`] = "Justification is required";
      }

      if (new Date(activity.dateAchieved) > new Date(activity.targetDate)) {
        errors[`dateAchieved_${index}`] =
          "Date achieved cannot be later than the target date";
      } else {
        delete errors[`dateAchieved_${index}`];
      }
      
      if (new Date(activity.targetDate) < new Date(activity.dateAchieved)) {
        errors[`targetDate_${index}`] =
          "Target date cannot be earlier than the date achieved";
      } else {
        delete errors[`targetDate_${index}`];
      }
      

      // if (
      //   activity.resourceallocated ||
      //   activity.resourceallocated.trim() === ""
      // ) {
      //   errors[`resourceallocated_${index}`] = "Resource Allocated is required";
      // }
      if (!activity.milestone || activity.milestone.trim() === "") {
        errors[`milestones_${index}`] = "Milestone is required";
      }
      if (!activity.lessons || activity.lessons.trim() === "") {
        errors[`lessons_${index}`] = "milestones is required";
      }
      if (!activity.challenges || activity.challenges.trim() === "") {
        errors[`challenges_${index}`] = "Challenges is required";
      }
      if (!activity.comments || activity.comments.trim() === "") {
        errors[`comments_${index}`] = "comments is required";
      }
    });

    return errors;
  };

  // Update handleSubmitData to check validation before proceeding
  const handleSubmitData = async (submitType) => {
    if (!validateForm()) {
      setError("Please select project name");
      setSnackOpen(true);
      return;
    }

    const outcomeValidationErrors = validateOutcomeFields();
    const outputValidationErrors = validateOutputFields();
    const activityValidationErrors = validateActivityFields();

    if (
      formType === "output" &&
      Object.keys(outputValidationErrors).length > 0
    ) {
      setOutputError(outputValidationErrors);
      setError("Please fill all required fields before adding new output");
      setSnackOpen(true);
      return;
    }

    if (
      formType === "activity" &&
      Object.keys(activityValidationErrors).length > 0
    ) {
      setActivityError(activityValidationErrors);
      setError("Please fill all required fields before adding new activity");
      setSnackOpen(true);
      return;
    }

    if (
      formType === "outcome" &&
      Object.keys(outcomeValidationErrors).length > 0
    ) {
      setOutcomeError(outcomeValidationErrors);
      setError("Please fill all required fields before adding new outcome");
      setSnackOpen(true);
      return;
    }

    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) {
        console.error("User data not found. Redirecting to login.");
        return;
      }
      const {
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
      } = userData;

      // const currentValue = formData.current?.value || 0;
      // const targetValue = formData.target?.value || 0;
      // const baselineValue = formData.baseline?.value || 0;

      // // Calculate percentage achievement with % symbol
      // const achievementPercentage = calculatePercentage(
      //   currentValue,
      //   targetValue
      // );

      // Add currency symbol to the values
      const currencySymbol = getCurrencySymbol(currency);

      if (formType === "outcome") {
        const indicatorPromis = [];
        outcomeState.forEach((outcome, index) => {
          const formattedData = {
            ...outcome,
            projectName: formData.projectName,
            baseline: {
              value: outcome.baseline,
              currency,
              symbol: currencySymbol,
            },
            target: {
              value: outcome.target,
              currency,
              symbol: currencySymbol,
            },
            current: {
              value: outcome.current,
              currency,
              symbol: currencySymbol,
            },
            resourceallocated: outcome.resourceallocated
              ? {
                  value: outcome.resourceallocated,
                  currency,
                  symbol: currencySymbol,
                }
              : null,
            percentageAchievement: outcome.achievement,
          };

          const endpoint =
            submitType === "draft"
              ? "/api/data-collection?isDraft=true"
              : "/api/data-collection?isDraft=false";

          const responsePromise = apiService.post(endpoint, {
            ...formattedData,
            type: formType,
            selectedOption,
            regionalNetworkOption,
            memberStateOption,
            countryOption,
            submissionType: submitType,
            submissionDate: new Date().toISOString(),
          });
          indicatorPromis.push(responsePromise);
        });
        const responses = await Promise.all(indicatorPromis);
        if (responses) {
          const successMessages = {
            draft: "Saved as draft successfully!",
            final: "Submitted successfully!",
            review: "Sent for review successfully!",
          };
          setSuccess(successMessages[submitType]);
          setOutcomeState([{}]);
          setError(null);
          setSnackOpen(true);

          if (submitType === "final" || submitType === "review") {
            setFormData({
              type: formType,
            });
            setFormErrors({});
          }
        }
      } else if (formType === "output") {
        const indicatorPromis = [];
        outputState.forEach((output, index) => {
          const formattedData = {
            ...output,
            projectName: formData.projectName,
            baseline: {
              value: output.baseline,
              currency,
              symbol: currencySymbol,
            },
            target: {
              value: output.target,
              currency,
              symbol: currencySymbol,
            },
            current: {
              value: output.current,
              currency,
              symbol: currencySymbol,
            },
            resourceallocated: output.resourceallocated
              ? {
                  value: output.resourceallocated,
                  currency,
                  symbol: currencySymbol,
                }
              : null,
            percentageAchievement: output.achievement,
          };

          const endpoint =
            submitType === "draft"
              ? "/api/data-collection?isDraft=true"
              : "/api/data-collection?isDraft=false";

          const responsePromise = apiService.post(endpoint, {
            ...formattedData,
            type: formType,
            selectedOption,
            regionalNetworkOption,
            memberStateOption,
            countryOption,
            submissionType: submitType,
            submissionDate: new Date().toISOString(),
          });
          indicatorPromis.push(responsePromise);
        });
        const responses = await Promise.all(indicatorPromis);
        if (responses) {
          const successMessages = {
            draft: "Saved as draft successfully!",
            final: "Submitted successfully!",
            review: "Sent for review successfully!",
          };
          setSuccess(successMessages[submitType]);
          setOutputState([{}]);
          setError(null);
          setSnackOpen(true);

          if (submitType === "final" || submitType === "review") {
            setFormData({
              type: formType,
            });
            setFormErrors({});
          }
        }
      } else {
        const indicatorPromis = [];
        activityState.forEach((activity, index) => {
          const formattedData = {
            ...activity,
            projectName: formData.projectName,
            baseline: {
              value: activity.baseline,
              currency,
              symbol: currencySymbol,
            },
            target: {
              value: activity.target,
              currency,
              symbol: currencySymbol,
            },
            current: {
              value: activity.current,
              currency,
              symbol: currencySymbol,
            },
            resourceallocated: activity.resourceallocated
              ? {
                  value: activity.resourceallocated,
                  currency,
                  symbol: currencySymbol,
                }
              : null,
            percentageAchievement: activity.achievement,
          };

          const endpoint =
            submitType === "draft"
              ? "/api/data-collection?isDraft=true"
              : "/api/data-collection?isDraft=false";

          const responsePromise = apiService.post(endpoint, {
            ...formattedData,
            type: formType,
            selectedOption,
            regionalNetworkOption,
            memberStateOption,
            countryOption,
            submissionType: submitType,
            submissionDate: new Date().toISOString(),
          });
          indicatorPromis.push(responsePromise);
        });
        const responses = await Promise.all(indicatorPromis);
        if (responses) {
          const successMessages = {
            draft: "Saved as draft successfully!",
            final: "Submitted successfully!",
            review: "Sent for review successfully!",
          };
          setSuccess(successMessages[submitType]);
          setActivityState([{}]);
          setError(null);
          setSnackOpen(true);

          if (submitType === "final" || submitType === "review") {
            setFormData({
              type: formType,
            });
            setFormErrors({});
          }
        }
      }

      // Show success message
      setSnackOpen(true);
      
      // Optional: Reset form after short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000); // Wait 2 seconds before reload

    } catch (err) {
      setError("Error processing data. Please try again.");
      setSuccess(null);
      setSnackOpen(true);
      console.error("Error:", err);
    }
  };

  // outcome functions
  const handleOutcomeChange = (i, e) => {
    let newoutcomeState = [...outcomeState];
    newoutcomeState[i][e.target.name] = e.target.value;
    if (e.target.name === "current" || e.target.name === "target") {
      newoutcomeState[i][e.target.name] = e.target.value;
      newoutcomeState[i].achievement = calculatePercentage1(
        parseInt(newoutcomeState[i].current),
        parseInt(newoutcomeState[i].target)
      );
    }
    setOutcomeState(newoutcomeState);
  };

  const addOutcomeField = () => {
    const validationErrors = validateOutcomeFields();

    if (Object.keys(validationErrors).length > 0) {
      setOutcomeError(validationErrors);
      setError("Please fill all required fields before adding new outcome");
      setSnackOpen(true);
      return;
    }

    setOutcomeError({});
    setOutcomeState([...outcomeState, {}]);
  };

  // output functions
  const handleOutputChange = (i, e) => {
    let newoutputState = [...outputState];
    newoutputState[i][e.target.name] = e.target.value;
    if (e.target.name === "current" || e.target.name === "target") {
      newoutputState[i][e.target.name] = e.target.value;
      newoutputState[i].achievement = calculatePercentage1(
        parseInt(newoutputState[i].current),
        parseInt(newoutputState[i].target)
      );
    }
    if (
      e.target.name === "maleCount" ||
      e.target.name === "femaleCount" ||
      e.target.name === "otherCount"
    ) {
      newoutputState[i]["gender"] = {
        ...newoutputState[i]["gender"],
        [e.target.name]: e.target.value,
      };
    }

    if (
      e.target.name === "0-17" ||
      e.target.name === "18-30" ||
      e.target.name === "31-59" ||
      e.target.name === "over59"
    ) {
      newoutputState[i]["ageGroup"] = {
        ...newoutputState[i]["ageGroup"],
        [e.target.name]: e.target.value,
      };
    }

    setOutputState(newoutputState);
  };

  const addOutputField = () => {
    const validationErrors = validateOutputFields();

    if (Object.keys(validationErrors).length > 0) {
      setOutputError(validationErrors);
      // Show error message
      setError("Please fill all required fields before adding new output");
      setSnackOpen(true);
      return;
    }

    setOutputError({});
    setOutputState([...outputState, {}]);
  };

  // activity functions
  const handleActivityChange = (i, e) => {
    let newactivityState = [...activityState];
    newactivityState[i][e.target.name] = e.target.value;
    if (e.target.name === "current" || e.target.name === "target") {
      newactivityState[i][e.target.name] = e.target.value;
      newactivityState[i].achievement = calculatePercentage(
        parseInt(newactivityState[i].current),
        parseInt(newactivityState[i].target)
      );
    }
    setActivityState(newactivityState);
  };

  const addActivityField = () => {
    const validationErrors = validateActivityFields();

    if (Object.keys(validationErrors).length > 0) {
      setActivityError(validationErrors);
      // Show error message
      setError("Please fill all required fields before adding new activity");
      setSnackOpen(true);
      return;
    }

    setActivityError({});
    setActivityState([...activityState, {}]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Clear error for the field being changed
    setFormErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    if (name === "indicators") {
      // Handle multiple indicator selection
      setFormData((prevData) => ({
        ...prevData,
        [name]: typeof value === "string" ? value.split(",") : value,
      }));
    } else if (["baseline", "target", "current"].includes(name)) {
      const usdValue = convertToUSD(value, currency);
      setFormData((prevData) => ({
        ...prevData,
        [name]: {
          value: value,
          currency: currency,
          symbol: getCurrencySymbol(currency),
          usdValue: usdValue,
        },
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  // const handleAddNewIndicator = async () => {
  //   if (!newIndicator.trim()) {
  //     setError("Indicator name cannot be empty");
  //     setSnackOpen(true);
  //     return;
  //   }

  //   const userData = JSON.parse(localStorage.getItem("user"));
  //   if (!userData) {
  //     setError("User data not found");
  //     setSnackOpen(true);
  //     return;
  //   }

  //   try {
  //     const {
  //       selectedOption,
  //       regionalNetworkOption,
  //       memberStateOption,
  //       countryOption,
  //     } = userData;
  //     const response = await apiService.post("/api/indicators", {
  //       name: newIndicator.trim(),
  //       selectedOption,
  //       regionalNetworkOption,
  //       memberStateOption,
  //       countryOption,
  //       isCategory: true,
  //     });

  //     if (response.data) {
  //       setIndicators((prev) => [...prev, newIndicator.trim()]);
  //       setNewIndicator("");
  //       setIndicatorDialogOpen(false);
  //       setSuccess("Indicator added successfully");
  //       setSnackOpen(true);
  //     }
  //   } catch (error) {
  //     console.error("Error adding indicator:", error);
  //     setError("Failed to add indicator");
  //     setSnackOpen(true);
  //   }
  // };

  // const renderIndicatorField = () => (
  //   <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
  //     <FormControl fullWidth error={!!formErrors.indicators}>
  //       <InputLabel id="indicators-label">Indicators *</InputLabel>
  //       <Select
  //         labelId="indicators-label"
  //         name="indicators"
  //         multiple
  //         value={formData.indicators || []}
  //         onChange={handleInputChange}
  //         variant="outlined"
  //         label="Indicators *"
  //         required
  //         renderValue={(selected) => (
  //           <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
  //             {selected.map((value) => (
  //               <Typography key={value} component="span" sx={{ mr: 1 }}>
  //                 {value}
  //               </Typography>
  //             ))}
  //           </Box>
  //         )}
  //       >
  //         {indicators.map((indicator, index) => (
  //           <MenuItem key={index} value={indicator}>
  //             {indicator}
  //           </MenuItem>
  //         ))}
  //       </Select>
  //       {formErrors.indicators && (
  //         <FormHelperText error>{formErrors.indicators}</FormHelperText>
  //       )}
  //     </FormControl>
  //     <Button
  //       variant="contained"
  //       color="primary"
  //       onClick={() => setIndicatorDialogOpen(true)}
  //       sx={{ mt: 1 }}
  //       startIcon={<AddIcon />}
  //     >
  //       Add
  //     </Button>
  //   </Box>
  // );

  // Update the calculate percentage function to return integer with % symbol
  const calculatePercentage = (current, target) => {
    if (!current || !target) return "0%";
    const currentValue = parseFloat(current);
    const targetValue = parseFloat(target);
    if (targetValue === 0) return "0%";
    return `${Math.round(((targetValue-currentValue) / targetValue) * 100)}%`;
  };

  const calculatePercentage1 = (current, target,baseline) => {
    if (!current || !target) return "0%";
    const currentValue = parseFloat(current);
    const baselineValue = parseFloat(baseline);
    const targetValue = parseFloat(target);
    if (targetValue === 0) return "0%";
    return `${Math.round(((currentValue-baselineValue)/(targetValue-baselineValue)) * 100)}%`;
  };

  const handleReview = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setReviewDialogOpen(true);
  };

  // Update handleSubmit to show specific validation errors
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    handleSubmitData("final");
  };

  const handleReviewSubmit = () => {
    if (!validateForm()) {
      setError("Please fill in all required fields before submitting");
      setSnackOpen(true);
      return;
    }
    setReviewDialogOpen(false);
    handleSubmitData("review");
  };

  // Add handleSaveAsDraft function
  const handleSaveAsDraft = (e) => {
    e.preventDefault();
    handleSubmitData("draft");
  };

  const ReviewDialog = () => {
    const renderReviewField = (label, value) => (
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {value
            ? typeof value === "object" && value.value
              ? value.value
              : value
            : "Not provided"}
        </Typography>
      </Grid>
    );

    return (
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review {formType.charAt(0).toUpperCase() + formType.slice(1)} Details
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {renderReviewField(
              "Project Name",
              projects.find((p) => p._id === formData.projectName)?.projectName
            )}

            {formType === "outcome" && (
              <>
                {renderReviewField("Outcome Name", formData.name)}
                {renderReviewField("Baseline Value", formData.baseline)}
                {renderReviewField("Target Value", formData.target)}
                {renderReviewField("Current Value", formData.current)}
                {renderReviewField(
                  "Level of Achievement",
                  calculatePercentage(
                    formData.current?.value,
                    formData.target?.value
                  )
                )}
                {renderReviewField("Justification", formData.justify)}
                {renderReviewField("Indicators", formData.indicators)}
                {renderReviewField("Baseline Date", formData.dateAchieved)}
                {renderReviewField("Target Date", formData.targetDate)}
                {renderReviewField(
                  "Reporting Frequency",
                  formData.reportingFrequency
                )}
                {renderReviewField("Comments", formData.comments)}
              </>
            )}

            {formType === "output" && (
              <>
                {renderReviewField("Output Name", formData.name)}
                {renderReviewField("Baseline Output", formData.baseline)}
                {renderReviewField("Target Output", formData.target)}
                {renderReviewField("Actual Output", formData.current)}
                {renderReviewField(
                  "Level of Achievement",
                  calculatePercentage(
                    formData.current?.value,
                    formData.target?.value
                  )
                )}
                {renderReviewField("Justification", formData.justify)}
                {renderReviewField("Indicators", formData.indicators)}
                {renderReviewField("Baseline Date", formData.baselineDate)}
                {renderReviewField("Target Date", formData.targetDate)}
                {renderReviewField(
                  "Actual Output Date",
                  formData.actualOutputDate
                )}
                {renderReviewField(
                  "Reporting Frequency",
                  formData.reportingFrequency
                )}
                {renderReviewField("Comments", formData.comments)}
              </>
            )}

            {formType === "activity" && (
              <>
                {renderReviewField(
                  "Output Name",
                  data.find((o) => o._id === formData.outputname)?.name
                )}
                {renderReviewField("Activity Name", formData.activityname)}
                {renderReviewField("Description", formData.description)}
                {renderReviewField("Start Date", formData.dateAchieved)}
                {renderReviewField("End Date", formData.targetDate)}
                {renderReviewField("Status", formData.status)}
                {renderReviewField(
                  "Resources Allocated",
                  formData.resourceallocated
                )}
                {renderReviewField("Planned Cost", formData.target)}
                {renderReviewField("Actual Cost", formData.current)}
                {renderReviewField(
                  "Cost Variance",
                  calculatePercentage(
                    formData.current?.value || 0,
                    formData.target?.value || 0
                  )
                )}
                {renderReviewField("Justification", formData.justify)}
                {renderReviewField("Milestones", formData.milestone)}
                {renderReviewField("Lessons Learned", formData.lessons)}
                {renderReviewField("Challenges", formData.challenges)}
                {renderReviewField("Comments", formData.comments)}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            color="primary"
          >
            Confirm & Submit
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Add delete handler functions
  const handleDeleteOutcome = (indexToDelete) => {
    setOutcomeState(outcomeState.filter((_, index) => index !== indexToDelete));
    setOutcomeError({});
  };

  const handleDeleteOutput = (indexToDelete) => {
    setOutputState(outputState.filter((_, index) => index !== indexToDelete));
    setOutputError({});
  };

  const handleDeleteActivity = (indexToDelete) => {
    setActivityState(activityState.filter((_, index) => index !== indexToDelete));
    setActivityError({});
  };

  // Add this validation function before renderOutcomeFields
  const validateNumberInput = (e) => {
    const value = e.target.value;
    if (!/^\d*\.?\d*$/.test(value)) { // Only allow digits and one decimal point
      e.target.value = value.replace(/[^\d.]/g, ''); // Remove non-numeric chars except decimal
      return false;
    }
    return true;
  };

  // Update the render functions to include delete buttons
  const renderOutcomeFields = () => (
    <>
      {outcomeState.map((outcome, i) => (
        <Box key={i}>
          {i !== 0 && outcomeState.length > 1 && (  // Don't show delete button for first form (index 0)
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeleteOutcome(i)}
                sx={{ mt: 2 }}
              >
                Delete Outcome
              </Button>
            </Box>
          )}
          <Divider style={{ marginTop: 20 }} />
          <TextField
            fullWidth
            margin="normal"
            name="name"
            label="Outcome Name"
            variant="outlined"
            error={!!outcomeError[`name_${i}`]}
            helperText={outcomeError[`name_${i}`]}
            onChange={(e) => handleOutcomeChange(i, e)}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            name="baseline"
            label="Baseline Value"
            variant="outlined"
            type="text" // Changed from number to text for better control
            error={!!outcomeError[`baseline_${i}`]}
            helperText={outcomeError[`baseline_${i}`] || "Enter numbers only"}
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (validateNumberInput(e)) {
                handleOutcomeChange(i, e);
              }
            }}
            required
            InputProps={{
              inputProps: { min: 1 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Enter the baseline value in your chosen currency">
                    <IconButton edge="end">
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            name="target"
            label="Target Value"
            variant="outlined"
            type="text"
            error={!!outcomeError[`target_${i}`]}
            helperText={outcomeError[`target_${i}`] || "Enter numbers only"}
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (validateNumberInput(e)) {
                handleOutcomeChange(i, e);
              }
            }}
            required
            InputProps={{
              inputProps: { min: 1 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Enter the Target Value in your chosen currency">
                    <IconButton edge="end">
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            name="current"
            label="Current Value"
            variant="outlined"
            type="text"
            error={!!outcomeError[`current_${i}`]}
            helperText={outcomeError[`current_${i}`] || "Enter numbers only"}
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (validateNumberInput(e)) {
                handleOutcomeChange(i, e);
              }
            }}
            required
            InputProps={{
              inputProps: { min: 1 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Enter the Current Value in your chosen currency">
                    <IconButton edge="end">
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            name="achievement"
            label="Level of Achievement"
            error={!!outcomeError[`achievement_${i}`]}
            helperText={outcomeError[`achievement_${i}`]}
            variant="outlined"
            value={calculatePercentage1(
              parseInt(outcome?.current),
              parseInt(outcome?.target),
              parseInt(outcome?.baseline)
            )}
            InputProps={{
              readOnly: true,
            }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            margin="normal"
            name="justify"
            label="Justification"
            error={!!outcomeError[`justify_${i}`]}
            helperText={outcomeError[`justify_${i}`]}
            variant="outlined"
            // value={formData.justify || ""}
            onChange={(e) => handleOutcomeChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />

          {/* {renderIndicatorField()} */}

          {/* <FormControl fullWidth margin="normal" error={!!formErrors.indicators}>
        <InputLabel id="indicators-label">Outcome Indicators*</InputLabel>
        <Select
          labelId="indicators-label"
          name="indicators"
          multiple
          // value={formData.indicators || []}
          onChange={e=>handleOutcomeChange(i,e)}
          variant="outlined"
          label="Outcome Indicators"
          required
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Typography key={value} component="span" sx={{ mr: 1 }}>
                  {value},
                </Typography>
              ))}
            </Box>
          )}
        >
          {indicators.map((indicator, index) => (
            <MenuItem key={index} value={indicator}>
              {indicator}
            </MenuItem>
          ))}
        </Select>
        {formErrors.indicators && (
          <FormHelperText>{formErrors.indicators}</FormHelperText>
        )}
      </FormControl> */}

          <TextField
            fullWidth
            margin="normal"
            name="dateAchieved"
            label="Date of Baseline"
            error={!!outcomeError[`dateAchieved_${i}`]}
            helperText={outcomeError[`dateAchieved_${i}`]}
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            // value={formData.dateAchieved || ""}
            onChange={(e) => handleOutcomeChange(i, e)}
            required
          />

          
          <TextField
            fullWidth
            margin="normal"
            name="targetDate"
            label="Target Date"
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            error={!!outcomeError[`targetDate_${i}`]}
            helperText={outcomeError[`targetDate_${i}`]}
            onChange={(e) => handleOutcomeChange(i, e)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            name="actualOutputDate"
            label="Reporting Date"
            error={!!outcomeError[`actualOutputDate_${i}`]}
            helperText={outcomeError[`actualOutputDate_${i}`]}
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            // value={formData.actualOutputDate || ""}
            onChange={(e) => handleOutcomeChange(i, e)}
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Frequency of Reporting*</InputLabel>
            <Select
              name="reportingFrequency"
              // value={formData.reportingFrequency || ""}
              onChange={(e) => handleOutcomeChange(i, e)}
              label="Frequency of Reporting"
              required
            >
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Quarterly">Quarterly</MenuItem>
              <MenuItem value="Annually">Annually</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            name="comments"
            label="Comments"
            variant="outlined"
            error={!!outcomeError[`comments_${i}`]}
            helperText={outcomeError[`comments_${i}`]}
            onChange={(e) => handleOutcomeChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />
        </Box>
      ))}
    </>
  );

  const renderOutputFields = () => (
    <>
      {outputState.map((output, i) => (
        <Box key={i}>
          {i !== 0 && outputState.length > 1 && (  // Don't show delete button for first form (index 0)
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeleteOutput(i)}
                sx={{ mt: 2 }}
              >
                Delete Output
              </Button>
            </Box>
          )}
          <Divider style={{ marginTop: 20 }} />
          <TextField
            fullWidth
            margin="normal"
            name="name"
            label="Output Name"
            variant="outlined"
            onChange={(e) => handleOutputChange(i, e)}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            name="baseline"
            label="Baseline Value"
            variant="outlined"
            type="text" // Changed from number to text for better control
            error={!!outputError[`baseline_${i}`]}
            helperText={outputError[`baseline_${i}`] || "Enter numbers only"}
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (validateNumberInput(e)) {
                handleOutputChange(i, e);
              }
            }}
            required
            InputProps={{
              inputProps: { min: 1 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Enter the baseline value in your chosen currency">
                    <IconButton edge="end">
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            name="target"
            label="Target Value"
            variant="outlined"
            type="text"
            error={!!outputError[`target_${i}`]}
            helperText={outputError[`target_${i}`] || "Enter numbers only"}
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (validateNumberInput(e)) {
                handleOutputChange(i, e);
              }
            }}
            required
            InputProps={{
              inputProps: { min: 1 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Enter the Target Value in your chosen currency">
                    <IconButton edge="end">
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            name="current"
            label="Current Value"
            variant="outlined"
            type="text"
            error={!!outputError[`current_${i}`]}
            helperText={outputError[`current_${i}`] || "Enter numbers only"}
            onKeyPress={(e) => {
              if (!/[0-9.]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              if (validateNumberInput(e)) {
                handleOutputChange(i, e);
              }
            }}
            required
            InputProps={{
              inputProps: { min: 1 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Enter the Current Value in your chosen currency">
                    <IconButton edge="end">
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            name="achievement"
            label="Level of Achievement"
            error={!!outputError[`achievement_${i}`]}
            helperText={outputError[`achievement_${i}`]}
            variant="outlined"
            value={calculatePercentage1(
              parseInt(output?.current),
              parseInt(output?.target),
              parseInt(output?.baseline)
            )}
            InputProps={{
              readOnly: true,
            }}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="showGender"
                checked={gender}
                onChange={() => setGender(!gender)}
              />
            }
            label={<Typography sx={{ my: 2 }}>Add gender metrics.</Typography>}
          />
          {gender && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="maleCount"
                  label="Number of Male"
                  variant="outlined"
                  type="number"
                  onChange={(e) => handleOutputChange(i, e)}
                  InputProps={{
                    inputProps: { min: 1 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Enter the number of women that participated.">
                          <IconButton edge="end">
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="femaleCount"
                  label="Number of Female"
                  variant="outlined"
                  type="number"
                  onChange={(e) => handleOutputChange(i, e)}
                  InputProps={{
                    inputProps: { min: 1 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Enter the number of women that participated.">
                          <IconButton edge="end">
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="otherCount"
                  label="Number of those with other gender"
                  variant="outlined"
                  type="number"
                  onChange={(e) => handleOutputChange(i, e)}
                  InputProps={{
                    inputProps: { min: 1 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Enter the number of those with other gender.">
                          <IconButton edge="end">
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          <FormControlLabel
            control={
              <Checkbox
                name="disability"
                checked={disability}
                onChange={() => setDisability(!disability)}
              />
            }
            label={
              <Typography sx={{ my: 2 }}>Add disability metrics.</Typography>
            }
          />

          {disability && (
            <TextField
              fullWidth
              margin="normal"
              name="disabledCount"
              label="Number of people with disabilities"
              variant="outlined"
              type="number"
              onChange={(e) => handleOutputChange(i, e)}
              InputProps={{
                inputProps: { min: 0 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Enter the number of those with disablities.">
                      <IconButton edge="end">
                        <Info />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                name="showAgeGroup"
                checked={ageGroup}
                onChange={() => setAgeGroup(!ageGroup)}
              />
            }
            label={
              <Typography sx={{ my: 2 }}>Add age group metrics.</Typography>
            }
          />

          {ageGroup && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Age Group
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="0-17"
                  label="Number of those between the age of 0-17"
                  variant="outlined"
                  type="number"
                  onChange={(e) => handleOutputChange(i, e)}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="18-30"
                  label="Number of those between the age of 18-30"
                  variant="outlined"
                  type="number"
                  onChange={(e) => handleOutputChange(i, e)}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="31-59"
                  label="Number of those between the age of 31-59"
                  variant="outlined"
                  type="number"
                  onChange={(e) => handleOutputChange(i, e)}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  name="over59"
                  label="Number of those over 59"
                  variant="outlined"
                  type="number"
                  onChange={(e) => handleOutputChange(i, e)}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
            </Grid>
          )}

          <TextField
            fullWidth
            margin="normal"
            name="justify"
            label="Justification"
            error={!!outputError[`justify_${i}`]}
            helperText={outputError[`justify_${i}`]}
            variant="outlined"
            // value={formData.justify || ""}
            onChange={(e) => handleOutputChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />
          <TextField
            fullWidth
            margin="normal"
            name="dateAchieved"
            label="Date of Baseline"
            error={!!outputError[`dateAchieved_${i}`]}
            helperText={outputError[`dateAchieved_${i}`]}
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            // value={formData.dateAchieved || ""}
            onChange={(e) => handleOutputChange(i, e)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            name="targetDate"
            label="Target Date"
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            error={!!outputError[`targetDate_${i}`]}
            helperText={outputError[`targetDate_${i}`]}
            onChange={(e) => handleOutputChange(i, e)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            name="actualOutputDate"
            label="Reporting Date"
            error={!!outputError[`actualOutputDate_${i}`]}
            helperText={outputError[`actualOutputDate_${i}`]}
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            // value={formData.actualOutputDate || ""}
            onChange={(e) => handleOutputChange(i, e)}
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Frequency of Reporting*</InputLabel>
            <Select
              name="reportingFrequency"
              // value={formData.reportingFrequency || ""}
              onChange={(e) => handleOutputChange(i, e)}
              label="Frequency of Reporting"
              required
            >
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Quarterly">Quarterly</MenuItem>
              <MenuItem value="Annually">Annually</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            name="comments"
            label="Comments"
            variant="outlined"
            error={!!outputError[`comments_${i}`]}
            helperText={outputError[`comments_${i}`]}
            onChange={(e) => handleOutputChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />
        </Box>
      ))}
    </>
  );

  const renderActivityFields = () => (
    <>
      {activityState.map((activity, i) => (
        <Box key={i}>
          {i !== 0 && activityState.length > 1 && (  // Don't show delete button for first form (index 0)
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDeleteActivity(i)}
                sx={{ mt: 2 }}
              >
                Delete Activity
              </Button>
            </Box>
          )}
          <Divider style={{ marginTop: 20 }} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Output Name*</InputLabel>
            <Select
              name="outputname"
              onChange={(e) => handleActivityChange(i, e)}
              required
              label="Output Name"
            >
              {Array.isArray(data) &&
                data
                  // .filter(
                  //   (output) => output.projectName === formData.projectName
                  // )
                  .map((output) => (
                    <MenuItem key={output._id} value={output._id}>
                      {output.name}
                    </MenuItem>
                  ))}
            </Select>
            {isLoading && <FormHelperText>Loading outputs...</FormHelperText>}
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            name="activityname"
            label="Activity Name"
            error={!!activityError[`name_${i}`]}
            helperText={activityError[`name_${i}`]}
            variant="outlined"
            onChange={(e) => handleActivityChange(i, e)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            name="description"
            error={!!activityError[`description_${i}`]}
            helperText={activityError[`description_${i}`]}
            label="Activity Description"
            variant="outlined"
            onChange={(e) => handleActivityChange(i, e)}
            required
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            margin="normal"
            name="dateAchieved"
            label="Start Date"
            error={!!activityError[`dateAchieved_${i}`]}
            helperText={activityError[`dateAchieved_${i}`]}
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleActivityChange(i, e)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            name="targetDate"
            error={!!activityError[`targetDate_${i}`]}
            helperText={activityError[`targetDate_${i}`]}
            label="End Date"
            variant="outlined"
            type="date"
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleActivityChange(i, e)}
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Currency*</InputLabel>
            <Select
              name="currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              required
              label="Currency"
            >
              {currencyOptions.map((curr) => (
                <MenuItem key={curr.code} value={curr.code}>
                  {`${curr.symbol} - ${curr.name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography>Resources Allocated</Typography>
          <Grid container spacing={2}>
           
            {/* <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                margin="normal"
                name="resourceallocated"
                label="Resources Allocated"
                error={!!activityError[`resourceallocated_${i}`]}
                helperText={activityError[`resourceallocated_${i}`]}
                variant="outlined"
                onChange={(e) => handleActivityChange(i, e)}
                required
              />
            </Grid> */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                name="target"
                label="planned Cost"
                error={!!activityError[`target_${i}`]}
                helperText={activityError[`target_${i}`] || "Enter numbers only"}
                variant="outlined"
                type="text" // Changed from number to text for better control
                onKeyPress={(e) => {
                  if (!/[0-9.]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  if (validateNumberInput(e)) {
                    handleActivityChange(i, e);
                  }
                }}
                required
                InputProps={{
                  inputProps: { min: 1 },
                  startAdornment: (
                    <InputAdornment position="start">
                      {getCurrencySymbol(currency)}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Enter the Planned cost in your chosen currency">
                        <IconButton edge="end">
                          <Info />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                name="current"
                label="Actual Cost"
                variant="outlined"
                type="text"
                error={!!activityError[`current_${i}`]}
                helperText={activityError[`current_${i}`] || "Enter numbers only"}
                onKeyPress={(e) => {
                  if (!/[0-9.]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  if (validateNumberInput(e)) {
                    handleActivityChange(i, e);
                  }
                }}
                required
                InputProps={{
                  inputProps: { min: 1 },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                name="achievement"
                label="Cost Variance"
                error={!!activityError[`achievement_${i}`]}
                helperText={activityError[`achievement_${i}`]}
                variant="outlined"
                value={calculatePercentage(
                  parseInt(activity?.current),
                  parseInt(activity?.target)
                )}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status*</InputLabel>
            <Select
              name="status"
              label="Status"
              onChange={(e) => handleActivityChange(i, e)}
              required
            >
              <MenuItem value="Ongoing">Ongoing</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            name="justify"
            label="Justification"
            error={!!activityError[`justify_${i}`]}
            helperText={activityError[`justify_${i}`]}
            variant="outlined"
            onChange={(e) => handleActivityChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />

          <TextField
            fullWidth
            margin="normal"
            name="milestone"
            error={!!activityError[`milestones_${i}`]}
            helperText={activityError[`milestones_${i}`]}
            label="Milestones Achieved"
            variant="outlined"
            onChange={(e) => handleActivityChange(i, e)}
            required
            multiline
            rows={2}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Enter the Milestones Achieved">
                    <IconButton edge="end">
                      <Info />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            name="lessons"
            error={!!activityError[`lessons_${i}`]}
            helperText={activityError[`lessons_${i}`]}
            label="Lessons Learned"
            variant="outlined"
            onChange={(e) => handleActivityChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />

          <TextField
            fullWidth
            margin="normal"
            name="challenges"
            error={!!activityError[`challenges_${i}`]}
            helperText={activityError[`challenges_${i}`]}
            label="Challenges"
            variant="outlined"
            onChange={(e) => handleActivityChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />

          <TextField
            fullWidth
            margin="normal"
            name="comments"
            error={!!activityError[`comments_${i}`]}
            helperText={activityError[`comments_${i}`]}
            label="Comments"
            variant="outlined"
            onChange={(e) => handleActivityChange(i, e)}
            multiline
            rows={2}
            maxRows={10}
          />
        </Box>
      ))}
    </>
  );

  return (
    <Container maxWidth="md">
    
      <Paper elevation={3} style={{ padding: "16px" }} >
        <Typography variant="h5" gutterBottom>
          Data Collection Form
        </Typography>
        <FormControl fullWidth margin="normal">
          <FormControl fullWidth margin="normal">
            <InputLabel>Project Name*</InputLabel>
            <Select
              name="projectName"
              label="Project Name"
              value={formData.projectName || ""}
              onChange={handleInputChange}
              required
            >
              {projects.map((project) => (
                <MenuItem key={project._id} value={project._id}>
                  {project.projectName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Select
            value={formType}
            onChange={(e) => setFormType(e.target.value)}
            variant="outlined"
          >
            <MenuItem value="outcome">Outcome</MenuItem>
            <MenuItem value="output">Output</MenuItem>
            <MenuItem value="activity">Activity</MenuItem>
          </Select>
        </FormControl>
        <form onSubmit={handleSubmit}>
          {formType === "outcome" && renderOutcomeFields()}
          {formType === "output" && renderOutputFields()}
          {formType === "activity" && renderActivityFields()}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
            }}
          >
            {/* Add/field buttons */}
            {formType === "outcome" && outcomeState.length < 5 && (
              <Button
                variant="contained"
                color="primary"
                onClick={addOutcomeField}
              >
                Add outcome Indicator
              </Button>
            )}
            {formType === "output" && outputState.length < 5 && (
              <Button
                variant="contained"
                color="primary"
                onClick={addOutputField}
              >
                Add output Indicator
              </Button>
            )}
            {formType === "activity" && activityState.length < 5 && (
              <Button
                variant="contained"
                color="primary"
                onClick={addActivityField}
              >
                Add Activity
              </Button>
            )}
            
            {/* Action buttons */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveAsDraft}
            >
              SAVE AS DRAFT
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleReview}
            >
              REVIEW
            </Button>
            
            {/* Only show Submit button if user is not Member State */}
            {userType !== 'Member State' && (
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
              >
                SAVE & SUBMIT
              </Button>
            )}
          </Box>

          {/* <AddIndicatorDialog
            open={indicatorDialogOpen}
            onClose={() => setIndicatorDialogOpen(false)}
            value={newIndicator}
            onChange={setNewIndicator}
            onSubmit={handleAddNewIndicator}
          /> */}

          <ReviewDialog />
        </form>
      
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={success ? "success" : "error"}
        >
          {success || error}
        </Alert>
      </Snackbar>

      </Paper>
      
      </Container> 

  );
};

export default DataCollection;